import React, { useState, useEffect } from "react";
import { ProductVariants } from "../../../hooks/APIHooks";
import {
  FcAddDatabase,
  FcSearch,
  FcEditImage,
  FcDeleteDatabase,
  FcRefresh,
  FcCancel,
  FcInfo,
  FcSettings,
  FcList,
  FcOk,
  FcExpired,
  FcDownRight
} from "react-icons/fc";

const VariantManager = () => {
  const [variants, setVariants] = useState([]);
  const [variantName, setVariantName] = useState("");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [optionValue, setOptionValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchVariants();
  }, []);

  const fetchVariants = async () => {
    try {
      setLoading(true);
      const response = await fetch(ProductVariants);
      const data = await response.json();
      setVariants(data);
    } catch (error) {
      console.error("Error fetching variants:", error);
      setMessage("Error fetching variants");
    } finally {
      setLoading(false);
    }
  };

  const addVariant = async () => {
    if (!variantName.trim()) {
      setMessage("Please enter a variant name");
      return;
    }

    try {
      const response = await fetch(ProductVariants, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variant_name: variantName }),
      });

      const result = await response.json();
      if (result.success) {
        setVariantName("");
        setMessage("Variant added successfully!");
        fetchVariants();
      } else {
        setMessage("Failed to add variant");
      }
    } catch (error) {
      console.error("Error adding variant:", error);
      setMessage("Error adding variant");
    }
  };

  const addOption = async () => {
    if (!selectedVariant || !optionValue.toString().trim()) {
      setMessage("Please select a variant and enter option value");
      return;
    }

    try {
      const response = await fetch(ProductVariants, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variant_id: selectedVariant,
          option_value: optionValue.toString(),
        }),
      });

      const result = await response.json();
      if (result.success) {
        setOptionValue("");
        setMessage("Option added successfully!");
        fetchVariants();
      } else {
        setMessage("Failed to add option");
      }
    } catch (error) {
      console.error("Error adding option:", error);
      setMessage("Error adding option");
    }
  };

  const deleteVariant = async (variantId, variantName) => {
    if (!window.confirm(`Are you sure you want to delete "${variantName}"? This will also delete all its options.`)) {
      return;
    }

    try {
      const response = await fetch(ProductVariants, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variant_id: variantId }),
      });

      const result = await response.json();
      if (result.success) {
        setMessage("Variant deleted successfully!");
        fetchVariants();
      } else {
        setMessage("Failed to delete variant");
      }
    } catch (error) {
      console.error("Error deleting variant:", error);
      setMessage("Error deleting variant");
    }
  };

  const deleteOption = async (optionId, optionValue) => {
    if (!window.confirm(`Are you sure you want to delete option "${optionValue}"?`)) {
      return;
    }

    try {
      const response = await fetch(ProductVariants, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ option_id: optionId }),
      });

      const result = await response.json();
      if (result.success) {
        setMessage("Option deleted successfully!");
        fetchVariants();
      } else {
        setMessage("Failed to delete option");
      }
    } catch (error) {
      console.error("Error deleting option:", error);
      setMessage("Error deleting option");
    }
  };

  const renameVariant = async (variantId, currentName) => {
    if (!variantName.trim() || variantName === currentName) {
      setSelectedVariant(null);
      setVariantName("");
      return;
    }

    try {
      const response = await fetch(ProductVariants, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variant_id: variantId,
          variant_name: variantName,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setVariantName("");
        setSelectedVariant(null);
        setMessage("Variant renamed successfully!");
        fetchVariants();
      } else {
        setMessage("Failed to rename variant");
      }
    } catch (error) {
      console.error("Error renaming variant:", error);
      setMessage("Error renaming variant");
    }
  };

  const renameOption = async (optionId, currentValue) => {
    if (!newOptionValue.toString().trim() || newOptionValue.toString() === currentValue.toString()) {
      setSelectedOption(null);
      setNewOptionValue("");
      return;
    }

    try {
      const response = await fetch(ProductVariants, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          option_id: optionId,
          option_value: newOptionValue.toString(),
        }),
      });

      const result = await response.json();
      if (result.success) {
        setNewOptionValue("");
        setSelectedOption(null);
        setMessage("Option renamed successfully!");
        fetchVariants();
      } else {
        setMessage("Failed to rename option");
      }
    } catch (error) {
      console.error("Error renaming option:", error);
      setMessage("Error renaming option");
    }
  };

  const filteredVariants = variants.filter(variant =>
    variant.variant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    variant.options.some(option => 
      option.option_value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FcSettings className="text-2xl" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-800">Variant Manager</h1>
                <p className="text-sm text-gray-600">Manage product variants and options</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={fetchVariants}
                className="flex items-center space-x-2 bg-white text-gray-700 px-3 py-2 rounded-lg border hover:bg-gray-50 transition-colors text-sm"
              >
                <FcRefresh />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
            message.includes("Error") || message.includes("Failed") ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
          }`}>
            <div className="flex items-center">
              <FcInfo className="mr-2" />
              <span className="text-sm">{message}</span>
            </div>
            <button onClick={() => setMessage("")} className="text-gray-500 hover:text-gray-700">×</button>
          </div>
        )}

        {/* Add Variant Section */}
        <div className="bg-white p-4 rounded-lg border mb-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <FcAddDatabase className="mr-2" />
            Add New Variant
          </h2>

          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Variant Name *</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="e.g., Color, Size, Material, Weight"
                value={variantName}
                onChange={(e) => setVariantName(e.target.value)}
              />
            </div>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={addVariant}
              disabled={!variantName.trim()}
            >
              <FcAddDatabase />
              <span>Add Variant</span>
            </button>
          </div>
        </div>

        {/* Add Option Section */}
        <div className="bg-white p-4 rounded-lg border mb-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <FcList className="mr-2" />
            Add Option to Variant
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Select Variant *</label>
              <select
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                onChange={(e) => setSelectedVariant(e.target.value)}
                value={selectedVariant}
              >
                <option value="">Choose a variant</option>
                {variants.map((variant) => (
                  <option key={variant.variant_id} value={variant.variant_id}>
                    {variant.variant_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Option Value *</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="e.g., Red, Large, 10, 2.5, Cotton"
                  value={optionValue}
                  onChange={(e) => setOptionValue(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Text or numbers are allowed</p>
              </div>
              <button
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                onClick={addOption}
                disabled={!selectedVariant || !optionValue.toString().trim()}
              >
                <FcOk />
                <span>Add Option</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white p-4 rounded-lg border mb-4">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                <FcSearch className="mr-1" />
                Search Variants & Options
              </label>
              <input
                type="text"
                placeholder="Search by variant name or option value..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1.5 rounded">
              {filteredVariants.length} of {variants.length} variants
            </div>
          </div>
        </div>

        {/* Variants Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h3 className="font-bold text-gray-800 flex items-center space-x-2 text-sm">
              <FcSettings />
              <span>Product Variants</span>
              <span className="text-gray-500 font-normal">({filteredVariants.length})</span>
            </h3>
          </div>

          <div className="overflow-x-auto">
            {loading && variants.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-600">Loading variants...</span>
                </div>
              </div>
            ) : filteredVariants.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredVariants.map((variant) => (
                  <div key={variant.variant_id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <FcSettings className="text-lg" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 text-sm">{variant.variant_name}</h4>
                          <p className="text-xs text-gray-600">ID: {variant.variant_id}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => {
                            setSelectedVariant(variant.variant_id);
                            setVariantName(variant.variant_name);
                          }}
                          className="bg-blue-100 text-blue-600 p-1.5 rounded hover:bg-blue-200 transition-colors"
                          title="Rename Variant"
                        >
                          <FcEditImage className="text-xs" />
                        </button>
                        <button
                          onClick={() => deleteVariant(variant.variant_id, variant.variant_name)}
                          className="bg-red-100 text-red-600 p-1.5 rounded hover:bg-red-200 transition-colors"
                          title="Delete Variant"
                        >
                          <FcDeleteDatabase className="text-xs" />
                        </button>
                      </div>
                    </div>

                    {/* Rename Variant Form */}
                    {selectedVariant === variant.variant_id && (
                      <div className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            className="flex-1 border border-yellow-300 rounded-lg p-2 focus:ring-1 focus:ring-yellow-500 focus:border-transparent text-sm"
                            placeholder="New variant name"
                            value={variantName}
                            onChange={(e) => setVariantName(e.target.value)}
                          />
                          <button
                            className="bg-yellow-600 text-white px-3 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm flex items-center space-x-1"
                            onClick={() => renameVariant(variant.variant_id, variant.variant_name)}
                          >
                            <FcEditImage />
                            <span>Rename</span>
                          </button>
                          <button
                            className="p-2 text-gray-500 hover:text-gray-700"
                            onClick={() => {
                              setSelectedVariant(null);
                              setVariantName("");
                            }}
                          >
                            <FcCancel />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Options List */}
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2 text-sm flex items-center">
                        <FcList className="mr-1" />
                        Options ({variant.options.length})
                      </h5>
                      
                      {variant.options.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {variant.options.map((option) => (
                            <div
                              key={option.option_id}
                              className="bg-gray-50 rounded-lg p-3 border flex items-center justify-between relative"
                            >
                              <span className="text-sm text-gray-800 font-medium">{option.option_value}</span>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => {
                                    setSelectedOption(option.option_id);
                                    setNewOptionValue(option.option_value);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Rename Option"
                                >
                                  <FcEditImage className="text-xs" />
                                </button>
                                <button
                                  onClick={() => deleteOption(option.option_id, option.option_value)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Delete Option"
                                >
                                  <FcDeleteDatabase className="text-xs" />
                                </button>
                              </div>

                              {/* Rename Option Form */}
                              {selectedOption === option.option_id && (
                                <div className="absolute top-full right-0 mt-2 p-3 bg-white border rounded-lg shadow-lg z-10 w-64">
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="text"
                                      className="flex-1 border rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                                      placeholder="New option value"
                                      value={newOptionValue}
                                      onChange={(e) => setNewOptionValue(e.target.value)}
                                    />
                                    <button
                                      className="bg-yellow-600 text-white px-2 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                                      onClick={() => renameOption(option.option_id, option.option_value)}
                                    >
                                      <FcEditImage />
                                    </button>
                                    <button
                                      className="p-2 text-gray-500 hover:text-gray-700"
                                      onClick={() => {
                                        setSelectedOption(null);
                                        setNewOptionValue("");
                                      }}
                                    >
                                      <FcCancel />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          <FcExpired className="text-xl mx-auto mb-1" />
                          No options added yet
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center">
                <FcSettings className="text-3xl mx-auto mb-2" />
                <h3 className="font-semibold text-gray-600 mb-1 text-sm">No variants found</h3>
                <p className="text-gray-500 mb-3 text-xs">
                  {searchTerm ? "Adjust your search" : "Add your first variant"}
                </p>
                <button 
                  onClick={() => {
                    setVariantName("");
                    setSelectedVariant(null);
                  }} 
                  className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                >
                  Add Variant
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantManager;